import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, BookOpen, Bookmark, Search } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { runAfterLeaveGuard } from '../../utils/draftLeaveGuard';
import { openBookChatPath } from '../../utils/openBookChat';
import { chatPaneSubject } from '../Chat/RoomChatPane';
import './MobileTabBar.css';

const TABS = [
  { id: 'chat', label: 'Chat', icon: MessageSquare, path: '/chat' },
  { id: 'bible', label: 'Book Notes', icon: BookOpen, path: '/bibles' },
  { id: 'ideas', label: 'Ideas', icon: Bookmark, path: '/bookmarks' },
  { id: 'search', label: 'Search', icon: Search, path: '/search' },
];

function getActiveTab(pathname) {
  if (pathname.startsWith('/chat')) return 'chat';
  if (pathname.startsWith('/bibles')) return 'bible';
  if (pathname.startsWith('/bookmarks') || pathname.startsWith('/project/bookmarks')) return 'ideas';
  if (pathname.startsWith('/search')) return 'search';
  return null;
}

/**
 * Chat on a book page opens that room's sheet.
 * With no book, go to the projects list.
 * Never bare /chat, which can open another book's Idea.
 */
export function chatTabAction(tab, context) {
  if (tab.id !== 'chat') return { type: 'navigate', to: tab.path };
  const bookOpen = Boolean(context?.currentStory?.id || context?.currentProject?.id);
  if (!bookOpen) return { type: 'navigate', to: '/projects' };
  if (chatPaneSubject(context?.pathname)) return { type: 'sheet' };
  const idea = openBookChatPath(context);
  if (idea) return { type: 'navigate', to: idea };
  return { type: 'none' };
}

export default function MobileTabBar({ onOpenRoomChat, chatOpen = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentSession, currentStory, currentProject } = useApp();
  const activeTab = getActiveTab(location.pathname);

  return (
    <nav className="mobile-tab-bar" aria-label="Main navigation">
      {TABS.map((tab) => {
        const Icon = tab.icon;
        const isActive = chatOpen ? tab.id === 'chat' : activeTab === tab.id;

        return (
          <button
            key={tab.id}
            className={`mobile-tab-bar__item ${isActive ? 'mobile-tab-bar__item--active' : ''}`}
            onClick={() => {
              runAfterLeaveGuard(() => {
                const action = chatTabAction(tab, {
                  pathname: location.pathname,
                  currentSession,
                  currentStory,
                  currentProject,
                });
                if (action.type === 'sheet') {
                  onOpenRoomChat?.();
                  return;
                }
                if (action.type === 'navigate') navigate(action.to);
              });
            }}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon size={20} />
            <span>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
